// Density follows the input in use (SIGNED_IN_PLAN 3.2), never the
// platform. AppShell sets data-density="pointer|touch" on <html> while it
// is mounted; tokens.css maps the attribute to the pointer or touch sizes.
// Signed-out pages never carry it and keep the (pointer: coarse) fallback.
//
// A pointerdown records its pointerType (mouse: pointer; touch or pen:
// touch; an empty type is ignored). The change applies on the following
// pointerup or pointercancel, in the next animation frame, so nothing
// reflows between pointerdown and click: the sidebar (210 to 280) and rows
// (30 to 44) would otherwise move the target under a hybrid device's click.

export function densityForPointerType(type) {
  if (type === "mouse") return "pointer";
  if (type === "touch" || type === "pen") return "touch";
  return null;
}

// The density at start: touch where the primary pointer is coarse.
export function initialDensity(matchMedia = typeof window !== "undefined" ? window.matchMedia : null) {
  try {
    return matchMedia && matchMedia.call(window, "(pointer: coarse)").matches ? "touch" : "pointer";
  } catch (e) {
    return "pointer";
  }
}

/**
 * The state machine, without the DOM: `apply(density)` runs when it
 * changes; `schedule(fn)` defers a change to the next frame.
 */
export function createDensityTracker({ initial = "pointer", apply = () => {}, schedule = null } = {}) {
  let current = initial;
  let pending = null;
  const later =
    schedule || ((fn) => (typeof requestAnimationFrame === "function" ? requestAnimationFrame(fn) : setTimeout(fn, 0)));
  return {
    get value() {
      return current;
    },
    pointerDown(e) {
      const d = densityForPointerType(e && e.pointerType);
      if (d) pending = d;
    },
    pointerUp() {
      if (!pending) return;
      const d = pending;
      pending = null;
      later(() => {
        if (d === current) return;
        current = d;
        apply(d);
      });
    },
  };
}

/**
 * Start tracking on the document: sets <html data-density> now and on every
 * change, and calls onChange(density). Returns the uninstall function, which
 * also removes the attribute.
 */
export function installDensity(onChange = () => {}) {
  if (typeof document === "undefined") return () => {};
  const root = document.documentElement;
  const set = (d) => {
    root.setAttribute("data-density", d);
    onChange(d);
  };
  const tracker = createDensityTracker({ initial: initialDensity(), apply: set });
  set(tracker.value);
  const down = (e) => tracker.pointerDown(e);
  const up = () => tracker.pointerUp();
  window.addEventListener("pointerdown", down, true);
  window.addEventListener("pointerup", up, true);
  window.addEventListener("pointercancel", up, true);
  return () => {
    window.removeEventListener("pointerdown", down, true);
    window.removeEventListener("pointerup", up, true);
    window.removeEventListener("pointercancel", up, true);
    root.removeAttribute("data-density");
  };
}
